import { ApiService } from './api-services/ApiService';
import { API_CONFIG } from '../config/apiConfig';
import { waitingRoomStatusSchema } from '../schemas/apiSchemas';
import { logger } from '../utils/logger';

export interface WaitingRoomStatus {
  teleconsultationId: string;
  position: number;
  estimatedWaitTime: number;
  totalPatients: number;
  doctorsAvailable: number;
  status: 'WAITING' | 'CALLED' | 'IN_CONSULTATION';
}

export interface JoinWaitingRoomRequest {
  teleconsultationId: string;
  patientId: number;
}

export interface LeaveWaitingRoomRequest {
  teleconsultationId: string;
  reason?: string;
}

class WaitingRoomService {
  private static instance: WaitingRoomService;

  static getInstance(): WaitingRoomService {
    if (!WaitingRoomService.instance) {
      WaitingRoomService.instance = new WaitingRoomService();
    }
    return WaitingRoomService.instance;
  }

  /**
   * Rejoindre une salle d'attente
   */
  async joinWaitingRoom(teleconsultationId: string): Promise<WaitingRoomStatus> {
    try {
      logger.debug('[WaitingRoomService] joinWaitingRoom appelé avec:', teleconsultationId);
      const response = await ApiService.getInstance().postNorm<WaitingRoomStatus>(
        API_CONFIG.ENDPOINTS.TELECONSULT.WAITING_ROOM.JOIN,
        { teleconsultationId }
      );
      logger.debug('[WaitingRoomService] Réponse backend:', response?.data);
      const parsed = response.data ? waitingRoomStatusSchema.safeParse(response.data) : null;
      if (parsed && parsed.success) return parsed.data as WaitingRoomStatus;
      throw new Error('Validation waiting-room/join invalide');
    } catch (error) {
      logger.error('Erreur lors de la connexion à la salle d\'attente:', error);
      throw error;
    }
  }

  /**
   * Quitter une salle d'attente
   */
  async leaveWaitingRoom(teleconsultationId: string, reason?: string): Promise<void> {
    try {
      await ApiService.getInstance().postNorm<void>(API_CONFIG.ENDPOINTS.TELECONSULT.WAITING_ROOM.LEAVE, {
        teleconsultationId,
        reason
      });
    } catch (error) {
      logger.error('Erreur lors de la sortie de la salle d\'attente:', error);
      throw error;
    }
  }

  /**
   * Obtenir le statut de la salle d'attente
   */
  async getWaitingRoomStatus(teleconsultationId: string): Promise<WaitingRoomStatus> {
    try {
      const response = await ApiService.getInstance().getNorm<WaitingRoomStatus>(
        API_CONFIG.ENDPOINTS.TELECONSULT.WAITING_ROOM.POSITION(teleconsultationId)
      );
      const parsed = response.data ? waitingRoomStatusSchema.safeParse(response.data) : null;
      if (parsed && parsed.success) return parsed.data as WaitingRoomStatus;
      throw new Error('Validation waiting-room/position invalide');
    } catch (error) {
      logger.error('Erreur lors de la récupération du statut:', error);
      throw error;
    }
  }

  /**
   * Obtenir la liste des patients en attente (pour les médecins)
   */
  async getWaitingPatients(): Promise<WaitingRoomStatus[]> {
    try {
      const response = await ApiService.getInstance().getNorm<WaitingRoomStatus[]>(API_CONFIG.ENDPOINTS.TELECONSULT.WAITING_ROOM.PATIENTS);
      const arr = Array.isArray(response.data) ? response.data : [];
      const filtered = arr.filter((it) => waitingRoomStatusSchema.safeParse(it).success);
      return filtered as unknown as WaitingRoomStatus[];
    } catch (error) {
      logger.error('Erreur lors de la récupération des patients en attente:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour la position dans la file d'attente
   */
  async updateQueuePosition(teleconsultationId: string, newPosition: number): Promise<void> {
    try {
      await ApiService.getInstance().putNorm(`${API_CONFIG.ENDPOINTS.TELECONSULT.WAITING_ROOM.BASE}/${teleconsultationId}/position`, {
        position: newPosition
      });
    } catch (error) {
      logger.error('Erreur lors de la mise à jour de la position:', error);
      throw error;
    }
  }
}

export const waitingRoomService = WaitingRoomService.getInstance();
export default waitingRoomService;
